import type { Dispatch, SetStateAction } from 'react'
import { createContext, useCallback, useMemo, useState } from 'react'
import type { AuthData } from '@mote/api'
import { useDeviceSignOutMutation, useTrackMutation } from '@mote/api'
import {
  canAccessWebLocalStorage,
  reallyTruthy,
  useEventListener
} from '@mote/helpers'
import {
  TRACK_EVENT_NAMES,
  WEB_STORAGE_PREFIX_KEYS
} from '@mote/helpers/constants'
import { assert } from '@mote/recipes'
import {
  broadcastToExtensions,
  deleteAllDataInStorage,
  readAllDataInStorage
} from '../shared/Auth'

type UserState = AuthData | null

interface CommonAuthContextValue {
  updateUser: Dispatch<SetStateAction<UserState>>
  getUser: () => AuthData
  logout: (
    forced?: boolean,
    isExtension?: boolean,
    status?: string | number
  ) => Promise<void>
}

export interface AuthenticatedAuthContextValue extends CommonAuthContextValue {
  user: AuthData
  isAuthenticated: true
}

export interface UnauthenticatedAuthContextValue
  extends CommonAuthContextValue {
  user: null
  isAuthenticated: false
}

export type AuthContextValue =
  | AuthenticatedAuthContextValue
  | UnauthenticatedAuthContextValue

export const AuthContext = createContext<AuthContextValue | null>(null)

export type AuthCustomLogoutEvent = CustomEvent<{
  isForced?: boolean
  isInExtension?: boolean
  error?: string
}>

interface AuthProviderProps {
  children: JSX.Element
}

export function AuthProvider(props: AuthProviderProps): JSX.Element {
  const deviceSignOutMutation = useDeviceSignOutMutation()
  const trackMutation = useTrackMutation()
  const [user, setUser] = useState<UserState>(() => {
    const authData = readAllDataInStorage()
    if (!authData.accessToken) {
      return null
    }
    return authData
  })

  function is403(status: string | number | undefined) {
    return status && status === (typeof status === 'string' ? '403' : 403)
  }

  const logout = useCallback(
    async (forced = false, isExtension = false, status?: string | number) => {
      if (!is403(status)) {
        try {
          //@ts-expect-error
          await deviceSignOutMutation.mutate()
        } catch {}
      }

      trackMutation.mutate!({
        variables: {
          event: TRACK_EVENT_NAMES.auth.logout,
          forced,
          origin: isExtension ? 'extension' : 'web'
        }
      })

      await broadcastToExtensions('logout', {})
      deleteAllDataInStorage()
      setUser(null)

      if (canAccessWebLocalStorage()) {
        window.localStorage.removeItem('mote-auth-accessToken-expired')
      }

      if (window.analytics) {
        window.analytics.reset()
      } else {
        console.error('No analytics.js to reset()')
      }
    },
    []
  )

  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (!user) return
      if (event.key !== `${WEB_STORAGE_PREFIX_KEYS}accessToken`) return
      if (reallyTruthy(event.newValue)) return

      setUser(null)
    },
    [user]
  )

  const getUser = useCallback<() => AuthData>(() => {
    assert(user !== null, 'AuthContext not authenticated')
    return user
  }, [user])

  useEventListener('storage', handleStorageChange)
  useEventListener(
    'auth:logout',
    (event: AuthCustomLogoutEvent) =>
      logout(
        Boolean(event.detail.isForced),
        Boolean(event.detail.isInExtension),
        event.detail.error
      ),
    document
  )

  const value = useMemo<AuthContextValue>(
    () =>
      user === null
        ? {
            updateUser: setUser,
            logout,
            user,
            getUser,
            isAuthenticated: false
          }
        : {
            updateUser: setUser,
            logout,
            user,
            getUser,
            isAuthenticated: true
          },
    [setUser, getUser, user]
  )

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  )
}
